import type { ClubPreset, EnvironmentConfig, SurfaceProperties, SurfaceType } from './types';

// ── Ball Physical Constants ──
export const BALL_MASS = 0.04593;                                   // kg
export const BALL_RADIUS = 0.02135;                                 // m (1.68 in diameter)
export const BALL_AREA = Math.PI * BALL_RADIUS * BALL_RADIUS;       // cross-section m^2
export const BALL_DIAMETER_VISUAL = 0.04267;                        // m for rendering

// ── Environment ──
export const AIR_DENSITY = 1.225;                                   // kg/m^3 sea level 15°C
export const GRAVITY = 9.81;                                        // m/s^2

// ── Default Environment Config ──
export const DEFAULT_ENVIRONMENT: EnvironmentConfig = {
  altitude: 0,            // feet (sea level)
  temperature: 59,        // °F (15°C — ISA standard, matches AIR_DENSITY = 1.225)
  windSpeed: 0,           // mph
  windDirection: 0,       // degrees
};

/**
 * Compute air density from altitude and temperature.
 *
 * Uses the ISA (International Standard Atmosphere) model:
 *   T(h) = T₀ - L×h  where L = 0.0065 K/m (tropospheric lapse rate)
 *   ρ/ρ₀ = (T(h)/T₀)^(g·M/(R·L) - 1)  (density ratio from hydrostatic eq.)
 *
 * The exponent g·M/(R·L) = 5.2559, so density exponent = 4.2559.
 * Temperature correction is applied for non-standard temperatures.
 *
 * At defaults (altitude=0, temperature=59°F/15°C) this returns exactly 1.225,
 * preserving backward compatibility.
 *
 * Validation:
 *   Denver (5,280 ft / 1,609 m): ρ ≈ 1.047 kg/m³ (~85% of sea level)
 *   Mexico City (7,350 ft / 2,240 m): ρ ≈ 0.975 kg/m³ (~80% of sea level)
 *
 * @param altitudeFeet - Altitude in feet above sea level
 * @param temperatureF - Temperature in degrees Fahrenheit
 * @returns Air density in kg/m³
 */
export function computeAirDensity(altitudeFeet: number, temperatureF: number): number {
  const altitudeMeters = altitudeFeet * 0.3048;
  const temperatureCelsius = (temperatureF - 32) * (5 / 9);
  const temperatureKelvin = temperatureCelsius + 273.15;

  // ISA lapse rate: L/T₀ = 0.0065 / 288.15 = 2.2558e-5 per meter
  // Density exponent: g·M/(R·L) - 1 = 5.2559 - 1 = 4.2559
  const tempRatio = 1 - 0.00002256 * altitudeMeters;
  const densityRatio = Math.pow(Math.max(tempRatio, 0.01), 4.2559);

  // Temperature correction for non-standard temperatures
  // At ISA standard (15°C = 288.15K), correction = 1.0
  const tempCorrection = 288.15 / temperatureKelvin;

  return 1.225 * densityRatio * tempCorrection;
}

// ── Wind Gradient (Atmospheric Boundary Layer Power Law) ──
// windAtHeight(y) = baseWind × (y / refHeight)^alpha
// alpha=0.2 is typical for open terrain (golf course)
export const WIND_GRADIENT_ALPHA = 0.2;
export const WIND_GRADIENT_REF_HEIGHT = 10;  // meters — standard anemometer height

/**
 * Compute wind speed at a given height using power-law wind gradient.
 * Wind is stronger at altitude (ball apex) and weaker near the ground.
 *
 * At y=10m (ref height), returns exactly baseSpeed (no scaling).
 * At y=0, returns 0 (no wind at ground level).
 *
 * @param baseSpeed - Wind speed at reference height (10m)
 * @param heightMeters - Current ball height in meters
 * @returns Scaled wind speed at the given height
 */
export function windSpeedAtHeight(baseSpeed: number, heightMeters: number): number {
  if (heightMeters <= 0) return 0;
  return baseSpeed * Math.pow(heightMeters / WIND_GRADIENT_REF_HEIGHT, WIND_GRADIENT_ALPHA);
}

// ── Spin Decay (velocity & density dependent) ──
// Base tau at reference conditions. Actual tau scales with airspeed and air density:
//   tau_eff = TAU_BASE × (V_REF / airspeed) × (RHO_REF / airDensity)
// Fast flight → more aerodynamic torque → faster decay (lower apex)
// Thin air (altitude) → less torque → slower decay (ball holds spin longer)
export const SPIN_DECAY_TAU = 22.0;                                 // base tau at reference conditions (seconds) — raised from 20 to offset velocity-dependent decay's wedge penalty
export const SPIN_DECAY_V_REF = 55.0;                               // m/s — calibration reference airspeed (~7i mid-flight)
export const SPIN_DECAY_V_MIN = 8.0;                                // m/s — floor to prevent infinite tau at very low speeds
export const SPIN_DECAY_RHO_REF = 1.225;                            // kg/m³ — ISA sea level density

// ── Gyroscopic Precession ──
// Aerodynamic torque causes the spin axis to precess toward the velocity vector.
// This reduces the lift-producing component of spin over time.
// Rate: ~1-3 deg/s for typical golf ball conditions.
export const PRECESSION_RATE = 0.08;                                 // precession coefficient — aggressive enough to reduce apex by 5-15% over full flight

// ── Aerodynamic Coefficients (dimpled golf ball empirical fits) ──
// Calibrated to match: Bearman & Harvey (1976), Quintavalla (2002),
// TrackMan PGA Tour carry distances, heights, and landing angles.
//
// Three-term drag model:
//   Cd(S) = CD_BASE + CD_SPIN * S/(S + S_half) + CD_QUAD * S²
//   1. CD_BASE: turbulent drag at zero spin (dimpled ball)
//   2. Michaelis-Menten spin-drag: saturating spin contribution
//   3. Quadratic spin-drag: turbulent wake interaction at high spin
//      Driver S≈0.08: +0.001 (negligible)
//      7-Iron S≈0.30: +0.016 (moderate — shortens carry to match)
//
// Lift model: Cl(S) = CL_MAX * S/(S + S_half)
//   Driver (S=0.08): CL≈0.126, 7-Iron (S=0.30): CL≈0.284
//
// Drag model: four terms — base + spin-saturating + quadratic + Gaussian peak
//   The peaked term targets the boundary layer transition zone (S≈0.20)
//   where dimple effectiveness changes, adding drag specifically for mid-irons.
//
export const CD_BASE = 0.130;         // base turbulent drag at zero spin (reduced to fix driver carry)
export const CD_SPIN = 0.195;         // additional drag from spin (Michaelis-Menten)
export const CD_S_HALF = 0.10;        // spin factor for half spin-drag
export const CD_QUAD = 0.18;          // quadratic spin-drag: CD_QUAD * S²

// Gaussian peaked drag term: extra drag centered on mid-irons (S≈0.20)
// Fixes the 5-iron/7-iron carry overshoot without affecting driver or wedges.
// Physics: boundary layer transition region where dimple effectiveness changes
export const CD_PEAK = 0.028;         // peak drag amplitude (slight raise from 0.025 → targets 5-iron)
export const CD_PEAK_S = 0.25;        // center spin factor (between 5-iron S=0.20 and 7-iron S=0.30)
export const CD_PEAK_W = 0.06;        // Gaussian half-width (covers 5i→7i, spares driver & wedges)

export const CL_MAX = 0.51;           // max lift coefficient (slight reduction for balance)
export const CL_S_HALF = 0.23;        // shifted left: +5% driver lift without inflating wedges
export const CL_QUAD = 0;             // no quadratic lift dampening needed with peaked drag

// ── Ascent Dampening (anti-balloon) ──
// During ascent, high-spin clubs "balloon" — the spin factor S increases as
// airspeed drops, creating excessive lift that inflates apex. Real balls
// experience progressive wake disruption and spin-induced drag that limits
// this ballooning. Dampening is spin-dependent and proportional to ascent angle.
//   effective_CL *= 1 - AD * (S/(S + AD_S_REF)) * ascent_ratio
export const ASCENT_DAMP = 0.20;      // ascent dampening strength (mild — just prevents balloon)
export const ASCENT_DAMP_S_REF = 0.12; // spin reference — targets mid/high-spin clubs

// ── Descent Dampening ──
// During descent, Magnus force has a forward component that prevents
// steep landing angles. Real balls experience spin axis precession and
// turbulent wake disruption that reduces Magnus effectiveness.
// Dampening is spin-dependent: higher spin → more precession → more dampening.
//   effective_CL *= 1 - DD * (S/(S + DD_S_REF)) * (descent_ratio²)
export const DESCENT_DAMP = 0.85;     // dampening strength
export const DESCENT_DAMP_S_REF = 0.10; // spin reference for dampening scaling

// ── Physics Timestep ──
export const PHYSICS_DT = 1 / 240;    // 240 Hz fixed step (4.167ms)
export const FLIGHT_SAMPLE_INTERVAL = 2; // record at 120 Hz; physics remains 240 Hz

// ── Ground Physics ──
export const BALL_REST_SPEED = 0.15;   // m/s — ball considered at rest
export const BALL_REST_TIME = 0.3;     // seconds below rest speed to stop

// ── Conversion ──
export const MPH_TO_MS = 0.44704;
export const M_TO_YARDS = 1.09361;
export const DEG_TO_RAD = Math.PI / 180;
export const RAD_TO_DEG = 180 / Math.PI;

// ── Surface Properties ──
export const SURFACE_PROPS: Record<SurfaceType, SurfaceProperties> = {
  // Effective flat-turf presets, not measured course/TrackMan surface parameters.
  // Contact friction handles slip/spin; rolling resistance acts only while settled.
  fairway: { restitution: 0.55, friction: 0.7, rollingResistance: 0.15, settledDrag: 2 },
  rough:   { restitution: 0.35, friction: 1.2, rollingResistance: 0.45, settledDrag: 4 },
  green:   { restitution: 0.50, friction: 0.5, rollingResistance: 0.09, settledDrag: 0 },
  bunker:  { restitution: 0.10, friction: 2.0, rollingResistance: 0.80, settledDrag: 8 },
};

// ── Club Presets (TrackMan PGA Tour averages) ──
// Sources: TrackMan Tour Averages 2023-2024, trackman.com/blog/golf/introducing-updated-tour-averages
export const CLUB_PRESETS: Record<string, ClubPreset> = {
  driver:  { name: 'Driver',         clubSpeed: 113, ballSpeed: 167, launchAngle: 10.9, spinRate: 2686,  spinAxis: 0, attackAngle: -1.3, clubPath: 0, faceAngle: 0 },
  '3wood': { name: '3 Wood',         clubSpeed: 107, ballSpeed: 158, launchAngle: 9.2,  spinRate: 3655,  spinAxis: 0, attackAngle: -2.9, clubPath: 0, faceAngle: 0 },
  '5iron': { name: '5 Iron',         clubSpeed: 94,  ballSpeed: 136, launchAngle: 14.1, spinRate: 5301,  spinAxis: 0, attackAngle: -3.7, clubPath: 0, faceAngle: 0 },
  '7iron': { name: '7 Iron',         clubSpeed: 90,  ballSpeed: 120, launchAngle: 16.3, spinRate: 7097,  spinAxis: 0, attackAngle: -4.3, clubPath: 0, faceAngle: 0 },
  '9iron': { name: '9 Iron',         clubSpeed: 85,  ballSpeed: 109, launchAngle: 20.4, spinRate: 8647,  spinAxis: 0, attackAngle: -4.7, clubPath: 0, faceAngle: 0 },
  pw:      { name: 'Pitching Wedge', clubSpeed: 83,  ballSpeed: 102, launchAngle: 24.2, spinRate: 9316,  spinAxis: 0, attackAngle: -5.0, clubPath: 0, faceAngle: 0 },
  sw:      { name: 'Sand Wedge',     clubSpeed: 77,  ballSpeed: 86,  launchAngle: 30.5, spinRate: 10200, spinAxis: 0, attackAngle: -5.5, clubPath: 0, faceAngle: 0 },
};

// ── Default Player Profile ──
export const DEFAULT_PLAYER_PROFILE = {
  height: 70,           // 5'10"
  wristToFloor: 34,     // inches
  weight: 180,          // lbs
  handedness: 'right' as const,
  handicap: 15,
};

// ── Temperature COR (Coefficient of Restitution) ──
// Golf ball COR drops in cold conditions due to reduced polymer compression.
// Reference: USGA/R&A research: COR drops ~0.008 per 20°F below 75°F.
// At 75°F (24°C), COR is at its rated maximum (0.830 for conforming balls).
// At 40°F (4°C), COR drops to ~0.816 — roughly 1.7% lower → 2-3% ball speed loss.
export const COR_REFERENCE_TEMP_F = 75;      // °F — ball COR is rated at this temp
export const COR_TEMP_COEFFICIENT = 0.00040;  // COR loss per °F below reference

/**
 * Compute temperature-adjusted COR multiplier.
 *
 * Returns a multiplier to apply to ball speed (not COR directly, since
 * ball speed ∝ COR × club speed). At reference temp (75°F) returns 1.0.
 * Colder temps return <1.0, warmer returns 1.0 (COR doesn't increase above max).
 *
 * Physics: Ball speed = COR × club speed (simplified).
 * If COR drops by 1.7%, ball speed drops by ~1.7%.
 *
 * @param temperatureF - Temperature in degrees Fahrenheit
 * @returns Ball speed multiplier (0.95–1.0 range for golf-relevant temps)
 */
export function temperatureCORMultiplier(temperatureF: number): number {
  if (temperatureF >= COR_REFERENCE_TEMP_F) return 1.0;
  const tempDrop = COR_REFERENCE_TEMP_F - temperatureF;
  const corLoss = tempDrop * COR_TEMP_COEFFICIENT;
  // COR multiplier maps to ball speed multiplier (approx linear for small changes)
  return Math.max(0.95, 1.0 - corLoss); // floor at 0.95 to prevent unrealistic values (~-125°F)
}

// ── Driving Range ──
export const RANGE_LENGTH = 400;     // meters
export const RANGE_WIDTH = 300;      // meters
export const YARD_MARKERS = [100, 150, 200, 250, 300]; // yards
